import React, { useMemo, useState } from 'react';
import { Doughnut, Bar } from 'react-chartjs-2';
import { formatCurrency } from '../../utils/formatters';

const InventoryView = ({ inventory, inventoryMetrics, sales }) => {
    
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());

    const stagnantStockMetrics = useMemo(() => {
        if (!inventory || !sales) return { value: 0, count: 0 };

        const ninetyDaysAgo = new Date();
        ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

        const recentSaleIds = new Set(
            sales
                .filter(s => new Date(s.saleDate) > ninetyDaysAgo)
                .flatMap(s => (s.items || []).map(i => i.productId))
        );

        const stagnantItems = inventory.filter(p => {
            const balance = p.initialQty - p.soldQty;
            return balance > 0 && !recentSaleIds.has(p.id);
        });
        
        const value = stagnantItems.reduce((acc, p) => acc + ((p.initialQty - p.soldQty) * p.cost), 0);
        return { value, count: stagnantItems.length };

    }, [inventory, sales]);
    
    const monthlyAcquisitionData = useMemo(() => {
        const monthsData = inventoryMetrics.monthlyAcquisition[selectedYear] || Array(12).fill(0);
        return {
            labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
            datasets: [{
                label: `Custo de Aquisição em ${selectedYear}`,
                data: monthsData,
                backgroundColor: 'rgba(215, 191, 189, 0.6)',
                borderColor: 'rgba(215, 191, 189, 1)',
                borderWidth: 1
            }]
        };
    }, [inventoryMetrics.monthlyAcquisition, selectedYear]);
    
    const topValueProductsChartData = useMemo(() => {
        const topProducts = inventoryMetrics.topValueProducts || [];
        return {
            labels: topProducts.map(p => p.name),
            datasets: [{
                label: 'Valor em Estoque (Custo)',
                data: topProducts.map(p => p.stockValue),
                backgroundColor: 'rgba(215, 191, 189, 0.6)',
                borderColor: 'rgba(215, 191, 189, 1)',
                borderWidth: 1
            }]
        };
    }, [inventoryMetrics.topValueProducts]);

    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-gray-800 text-center sm:text-left">Dashboard de Inventário</h2>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-[#F2DFDB] text-center sm:text-left">
              <h3 className="text-sm font-medium text-gray-500">Receita Potencial</h3>
              <p className="text-3xl font-bold text-green-600 mt-2">{formatCurrency(inventoryMetrics.projectedRevenue)}</p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-[#F2DFDB] text-center sm:text-left">
              <h3 className="text-sm font-medium text-gray-500">Custo do Estoque</h3>
              <p className="text-3xl font-bold text-gray-800 mt-2">{formatCurrency(inventoryMetrics.totalValue)}</p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-[#F2DFDB] text-center sm:text-left">
              <h3 className="text-sm font-medium text-gray-500">Lucro Potencial</h3>
              <p className="text-3xl font-bold text-green-600 mt-2">{formatCurrency(inventoryMetrics.potentialProfit)}</p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-[#F2DFDB] text-center sm:text-left">
              <h3 className="text-sm font-medium text-gray-500">Itens em Estoque</h3>
              <p className="text-3xl font-bold text-gray-800 mt-2">{inventoryMetrics.totalItems}</p>
            </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl border border-[#F2DFDB] text-center md:text-left">
                <h3 className="text-sm font-medium text-gray-500">Estoque Parado (+90 dias)</h3>
                <p className="text-3xl font-bold text-gray-800 mt-2">{stagnantStockMetrics.count} itens</p>
                <p className="text-lg font-medium text-gray-500">{formatCurrency(stagnantStockMetrics.value)} Total de Custo</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-[#F2DFDB] text-center md:text-left">
                <h3 className="text-sm font-medium text-gray-500">Perto do Vencimento (-30 dias)</h3>
                <p className="text-3xl font-bold text-gray-800 mt-2">{inventoryMetrics.expiringSoonCount}</p>
            </div>
             <div className="bg-white p-6 rounded-xl border border-[#F2DFDB] text-center md:text-left">
                <h3 className="text-sm font-medium text-gray-500">Estoque Baixo</h3>
                <p className="text-3xl font-bold text-gray-800 mt-2">{inventoryMetrics.lowStockCount || 0}</p>
            </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3 bg-white p-4 sm:p-6 rounded-2xl shadow-sm border border-[#F2DFDB]">
                <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4">
                    <h2 className="text-lg font-bold text-gray-800">Valor das Entradas em Estoque</h2>
                    <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} className="w-full sm:w-auto px-4 py-2 bg-white border border-[#D7BFBD] rounded-lg">
                        {inventoryMetrics.availableYears.map(year => <option key={year} value={year}>{year}</option>)}
                    </select>
                </div>
                <div className="h-72">
                    <Bar data={monthlyAcquisitionData} options={{ responsive: true, maintainAspectRatio: false, scales: { y: { beginAtZero: true } } }} />
                </div>
            </div>
            <div className="lg:col-span-2 bg-white p-4 sm:p-6 rounded-2xl shadow-sm border border-[#F2DFDB]">
                <h2 className="text-lg font-bold text-gray-800 mb-4 text-center">Valor do Estoque por Categoria</h2>
                <div className="h-72 flex justify-center">
                    <Doughnut data={inventoryMetrics.categoryChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }} />
                </div>
            </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm border border-[#F2DFDB]">
                <h2 className="text-lg font-bold text-gray-800 mb-4">Top 10 Produtos por Valor em Estoque</h2>
                <div className="h-96">
                    <Bar data={topValueProductsChartData} options={{ responsive: true, maintainAspectRatio: false, indexAxis: 'y', scales: { x: { ticks: { callback: value => formatCurrency(value) } } } }}/>
                </div>
            </div>
            <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm border border-[#F2DFDB]">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Valor do Estoque por Marca</h3>
              <div className="space-y-3 pr-2 overflow-y-auto" style={{maxHeight: '24rem'}}>
                {(inventoryMetrics.brandValues && inventoryMetrics.brandValues.length > 0) ? inventoryMetrics.brandValues.map(([brand, value]) => (
                  <div key={brand} className="flex justify-between items-center bg-[#fdfaf7] p-3 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 truncate">{brand}</p>
                    <p className="text-lg font-bold text-gray-800">{formatCurrency(value)}</p>
                  </div>
                )) : <p className="text-gray-500">Não há dados de marcas.</p>}
              </div>
            </div>
        </div>
      </div>
    );
};

export default InventoryView;
